import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../ThemeContext';
import ThemeToggle from './ThemeToggle';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const location = useLocation();
  const { isInstitutional } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setGenOpen(false); setMobileOpen(false); }, [location.pathname]);

  const navLinks = [
    { path: '/about', label: 'Missione' },
    { path: '/links', label: 'RoadMap' },
    { path: '/archivio', label: 'Archivio' },
    { path: '/admin', label: 'Admin' },
  ];

  const genItems = [
    { id: 'greatest', label: 'Greatest Gen', color: '#D4AF37' },
    { id: 'silent', label: 'Silent Gen', color: '#94A3B8' },
    { id: 'boomer', label: 'Baby Boomer', color: '#EF4444' },
    { id: 'boomers', label: 'Boomer', color: '#FF6347' },
    { id: 'x', label: 'Gen X', color: '#FFD700' },
    { id: 'millennials', label: 'Millennials', color: '#FF6B35' },
    { id: 'z', label: 'Gen Z', color: '#A855F7' },
    { id: 'alpha', label: 'Gen Alpha', color: '#00FF41' },
    { id: 'beta', label: 'Gen Beta', color: '#00DDFF' },
  ];

  const navBg = isInstitutional
    ? (scrolled ? 'bg-white/95 backdrop-blur-md border-b border-[#E2DED6]' : 'bg-white/80 backdrop-blur-sm')
    : (scrolled ? 'bg-black/90 backdrop-blur-md border-b border-white/10' : 'bg-transparent');

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <div className="flex flex-col">
            {isInstitutional ? (
              <>
                <span className="font-mono font-black text-lg md:text-2xl tracking-tight text-[#00FF41]"
                  style={{ textShadow: '0 0 10px rgba(0,255,65,0.3)' }}>
                  &lt;/AI_covo.tech&gt;<sup style={{ fontSize: '0.45em', verticalAlign: 'super', marginLeft: '1px', fontWeight: 400, color: '#00FF41', fontFamily: 'Georgia, serif' }}>&reg;</sup>
                </span>
                <span className="nav-payoff wave-text wave-text-slow font-mono text-[7px] md:text-[9px] font-black uppercase tracking-[0.2em] leading-none mt-0.5 text-center">
                  {'DIGITAL HUB SOCIALE'.split('').map((c, i) => <span key={i} style={{ color: '#fff' }}>{c === ' ' ? '\u00A0' : c}</span>)}
                </span>
              </>
            ) : (
              <>
                <span className="font-mono font-black text-lg md:text-2xl tracking-tight text-[#00FF41] neon-glow">
                  &lt;/AI_covo.tech&gt;<sup style={{ fontSize: '0.45em', verticalAlign: 'super', marginLeft: '1px', fontWeight: 400, color: '#00FF41', fontFamily: 'Georgia, serif' }}>&reg;</sup>
                </span>
                <span className="nav-payoff wave-text wave-text-slow font-mono text-[7px] md:text-[9px] font-black uppercase tracking-[0.2em] leading-none mt-0.5 text-center">
                  {'DIGITAL HUB SOCIALE'.split('').map((c, i) => <span key={i} style={{ color: '#fff' }}>{c === ' ' ? '\u00A0' : c}</span>)}
                </span>
              </>
            )}
          </div>
        </Link>

        <style>{`
            .nav-wave-letter {
              animation: navWave 2.5s ease-in-out calc(var(--d)) infinite;
              color: #C4B8A0;
            }
            .nav-wave-letter-light {
              animation: navWaveLight 2.5s ease-in-out calc(var(--d)) infinite;
              color: #8A7A62;
            }
            .nav-shimmer-dark {
              background: linear-gradient(90deg, #3B2506 0%, #5C3A0E 30%, #FFD89B 48%, #FFECD2 50%, #FFD89B 52%, #5C3A0E 70%, #3B2506 100%);
              background-size: 200% 100%;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
              animation: navShimmerDark 3s ease-in-out infinite;
            }
            @keyframes navShimmerDark {
              0% { background-position: 200% center; }
              100% { background-position: -200% center; }
            }
            @keyframes navWave {
              0%, 100% { transform: translateY(0); opacity: 0.75; }
              15% { transform: translateY(-3px); opacity: 1; text-shadow: 0 0 8px rgba(196,184,160,0.7), 0 0 14px rgba(255,255,255,0.3); color: #DDD2C0; }
              30% { transform: translateY(0); opacity: 0.75; }
            }
            @keyframes navWaveLight {
              0%, 100% { transform: translateY(0); opacity: 0.7; }
              15% { transform: translateY(-3px); opacity: 1; text-shadow: 0 0 6px rgba(138,122,98,0.4); color: #6B5B45; }
              30% { transform: translateY(0); opacity: 0.7; }
            }
          `}</style>

        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`text-sm uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
                isInstitutional
                  ? `font-semibold ${location.pathname === link.path ? 'text-[#C4532D]' : 'text-[#1B2A4A] hover:text-[#C4532D]'}`
                  : `font-mono ${location.pathname === link.path ? 'text-[#00FF41]' : 'text-white hover:text-[#00FF41]'}`
              }`}
              style={!isInstitutional ? { textShadow: location.pathname === link.path ? '0 0 10px rgba(0,255,65,0.5)' : '0 0 8px rgba(255,255,255,0.3)' } : { fontFamily: "'Titillium Web', sans-serif" }}
            >
              {!isInstitutional && (
                <span className="font-mono font-black text-[#00FF41] text-sm whitespace-nowrap">&lt;/</span>
              )}
              {link.label}
              {!isInstitutional && (
                <span className="font-mono font-black text-[#00FF41] text-sm">&gt;</span>
              )}
            </Link>
          ))}

          {/* GEN... Dropdown */}
          <div className="relative" data-testid="gen-dropdown">
            <button
              onClick={() => setGenOpen(!genOpen)}
              data-testid="gen-dropdown-btn"
              className={`text-sm uppercase tracking-wider transition-colors flex items-center gap-1.5 cursor-pointer ${
                isInstitutional
                  ? `font-semibold ${location.pathname === '/generazioni' ? 'text-[#C4532D]' : 'text-[#1B2A4A] hover:text-[#C4532D]'}`
                  : `font-mono ${location.pathname === '/generazioni' ? 'text-[#00FF41]' : 'text-white hover:text-[#00FF41]'}`
              }`}
              style={!isInstitutional ? { textShadow: '0 0 8px rgba(255,255,255,0.3)' } : { fontFamily: "'Titillium Web', sans-serif" }}
            >
              {!isInstitutional && <span className="font-mono font-black text-[#00FF41] text-sm whitespace-nowrap">&lt;/</span>}
              GEN...
              {!isInstitutional && <span className="font-mono font-black text-[#00FF41] text-sm">&gt;</span>}
              <svg className={`w-3 h-3 transition-transform ${genOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
            </button>
            {genOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setGenOpen(false)} />
                <div className={`absolute right-0 top-full mt-2 z-50 min-w-[220px] rounded-xl overflow-hidden shadow-2xl ${
                  isInstitutional ? 'bg-white border border-[#E2DED6]' : 'bg-[#0A0A0A] border border-zinc-700'
                }`} style={{ backdropFilter: 'blur(16px)' }}>
                  <Link to="/generazioni" onClick={() => setGenOpen(false)}
                    className={`block px-4 py-2.5 text-xs font-black uppercase tracking-widest border-b ${
                      isInstitutional ? 'text-[#C4532D] border-[#E2DED6] bg-[#FAF9F6]' : 'text-[#00FF41] border-zinc-800 bg-zinc-900/50'
                    }`}>
                    Tutte le Generazioni
                  </Link>
                  {genItems.map(g => (
                    <Link key={g.id} to={`/generazioni#${g.id}`} onClick={() => setGenOpen(false)}
                      data-testid={`gen-link-${g.id}`}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        isInstitutional ? 'hover:bg-[#FAF9F6] text-[#1B2A4A]' : 'hover:bg-zinc-800/60 text-zinc-300'
                      }`}
                      style={{ fontFamily: isInstitutional ? "'Titillium Web', sans-serif" : "'JetBrains Mono', monospace" }}>
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: g.color, boxShadow: `0 0 8px ${g.color}66` }} />
                      {g.label}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>

          <ThemeToggle />
        </div>

        <div className="flex md:hidden items-center gap-3">
          <ThemeToggle />
          <button
            data-testid="mobile-menu-btn"
            className={isInstitutional ? 'text-[#1B2A4A]' : 'text-white'}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className={`md:hidden backdrop-blur-md border-t ${
          isInstitutional
            ? 'bg-white/95 border-[#E2DED6]'
            : 'bg-black/95 border-white/10'
        }`}>
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMobileOpen(false)}
              className={`block px-6 py-4 text-sm uppercase tracking-wider border-b ${
                isInstitutional
                  ? 'font-semibold text-[#1B2A4A] hover:text-[#C4532D] border-[#E2DED6]/50'
                  : 'font-mono text-zinc-400 hover:text-[#00FF41] border-white/5'
              }`}
              style={isInstitutional ? { fontFamily: "'Titillium Web', sans-serif" } : undefined}
            >
              {link.label}
            </Link>
          ))}
          <div className={`border-b ${isInstitutional ? 'border-[#E2DED6]/50' : 'border-white/5'}`}>
            <Link to="/generazioni" onClick={() => setMobileOpen(false)}
              className={`block px-6 py-4 text-sm uppercase tracking-wider font-bold ${
                isInstitutional ? 'text-[#C4532D]' : 'font-mono text-[#00FF41]'
              }`}>
              GEN...
            </Link>
            <div className="px-6 pb-3 flex flex-wrap gap-2">
              {genItems.map(g => (
                <Link key={g.id} to={`/generazioni#${g.id}`} onClick={() => setMobileOpen(false)}
                  className={`text-xs px-3 py-1.5 rounded-full ${isInstitutional ? 'bg-[#FAF9F6] text-[#1B2A4A]' : 'bg-zinc-800 text-zinc-300'}`}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5" style={{ background: g.color }} />{g.label}
                </Link>
              ))}
            </div>
          </div>
          <Link to="/mentor" onClick={() => setMobileOpen(false)}
            data-testid="nav-mentor-mobile"
            className={`block px-6 py-4 text-sm uppercase tracking-wider border-b ${
              isInstitutional
                ? 'font-semibold text-[#1B2A4A] hover:text-[#C4532D] border-[#E2DED6]/50'
                : 'font-mono text-zinc-400 hover:text-[#00FF41] border-white/5'
            }`}
            style={{ fontFamily: "'Georgia', serif" }}>
            Μέντωρ
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
